import React, { useState } from 'react';
import { Button, Card, Table, TableProps, Typography } from 'antd';
import { SIGN_BY_TYPE_OF_TRANSACTION, TransactionsFilters } from '../../transactions';
import dayjs, { Dayjs } from 'dayjs';
import useGetTransactions from '../../../hooks/useGetTransactions';
import { ColumnsType } from 'antd/es/table';
import { ITransaction } from '../../../interfaces/transactions';
import { TransactionHelper } from '../../transactions/helper/transactions.helper';
import TransactionStateTag from '../../transactions/components/transaction-state-tag';
import { getProperDate } from '../../../utils/date';
import { FirstStepFormInputs, SecondStepFormInputs, ThirdStepFormInputs } from '../claim-new';

const TODAY_DATE = new Date();
const LAST_MONTH_DATE = new Date(new Date().setMonth(TODAY_DATE.getMonth() - 1));

type SearchTransactionsFilters = {
  dateRanges: [Dayjs, Dayjs];
};


type ThirdStepProps = {
  goToNext?: () => void | null;
  goToPrevious?: () => void | null;
  firstStepData?: FirstStepFormInputs | null;
  secondStepData?: SecondStepFormInputs | null;
  thirdStepData?: ThirdStepFormInputs | null;
  setThirdStepData?: (data: ThirdStepFormInputs) => void | null;
};

const ThirdStep: React.FC<ThirdStepProps>  = ({
  goToNext,
  goToPrevious,
  firstStepData,
  secondStepData,
  thirdStepData,
  setThirdStepData
}) => {
  const [startDate, setStartDate] = useState<Dayjs>(dayjs(LAST_MONTH_DATE));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs(TODAY_DATE));
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const { transactions, isLoading, pageInfo } = useGetTransactions(
    secondStepData?.card?.card_id,
    startDate.format('YYYY-MM-DDT00:00:00').toString(),
    endDate.format('YYYY-MM-DDT23:59:59').toString(),
    paginationModel.pageSize,
    paginationModel.page
  );

  const handleSearchClick = (data: SearchTransactionsFilters) => {
    setStartDate(data.dateRanges?.[0] ?? dayjs(LAST_MONTH_DATE));
    setEndDate(data.dateRanges?.[1] ?? dayjs(TODAY_DATE));
  };

  const getAmountColor = (transaction: ITransaction) => {
    let color = '#000000'

    if(transaction?.authorization?.approved || transaction?.authorization?.irc === '8444'){
      color = SIGN_BY_TYPE_OF_TRANSACTION.get(transaction.authorization.info.transaction.type) === '+' ? '#52c41a' : '#ff4d4f'
    }
    return color
  }

  const getDateByTransactionType = (transaction: ITransaction) => {
    return getProperDate(transaction?.authorization?.info?.transaction?.received_timestamp).concat('hs')
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  const columns: ColumnsType<ITransaction> = [
    {
      dataIndex: 'received_timestamp',
      key: 'received_timestamp',
      title: 'Fecha',
      render: (_, record) => {
        return getDateByTransactionType(record);
      },
    },
    {
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      title: 'Descripción',
      render: (_, record) => {
        return TransactionHelper.getMerchant(record.authorization.info.transaction.type, record.authorization.info.merchant.name)
      },
    },
    {
      dataIndex: 'final_amount',
      key: 'final_amount',
      title: 'Importe',
      render: (_, record) => {
        return (
          <Typography.Text  strong style={{color: getAmountColor(record)}}>
            {
              record?.authorization?.approved || record?.authorization?.irc === '8444' ? SIGN_BY_TYPE_OF_TRANSACTION.get(record.authorization.info.transaction.type) : ''
            }
            { TransactionHelper.getTransactionAmountFormatted(record.authorization.info.amounts.original.currency, record.authorization.info.amounts.original.amount)}
          </Typography.Text>
        );
      },
    },
    {
      dataIndex: 'type',
      key: 'type',
      title: 'Tipo',
      render: (_, record) => {
        return TransactionHelper.getTransactionType(record.authorization.info.transaction.type)
      },
    },
    {
      dataIndex: 'state',
      key: 'state',
      title: 'Estado',
      render: (_, record) => {
        return (
          <TransactionStateTag approved={record?.authorization?.approved} irc={ record?.authorization?.irc}/>
        );
      },
    },
    {
      dataIndex: 'display_message',
      key: 'display_message',
      title: 'Mensaje',
      render: (_, record) => {
        return !record?.authorization?.approved || record?.authorization?.irc !== '8444'
          ? record?.authorization?.info?.transaction?.display_message
          : '';
      },
    },
    {
      dataIndex: 'merchant_country',
      key: 'merchant_country',
      title: 'Pais',
      render: (_, record) => {
        return record?.authorization.info.merchant.country;
      },
    },
    {
      dataIndex: 'merchant_city',
      key: 'merchant_city',
      title: 'Ciudad',
      render: (_, record) => {
        return record?.authorization.info.merchant.city;
      },
    },
  ];

  const rowSelection: TableProps<ITransaction>['rowSelection'] = {
    type: "checkbox",
    selectedRowKeys: thirdStepData !== null ? thirdStepData.transaction.map(txn => txn.authorization.info.transaction.id) : [],
    onChange: (selectedRowKeys: React.Key[], selectedRows: ITransaction[]) => {
      setThirdStepData({transaction: selectedRows});
      console.log('selectedRow: ', selectedRows);
    }
  };

  return (
    <div>
      <Typography.Title level={2}>Transacciones</Typography.Title>
      <Card
        variant="borderless"
        style={{
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          transform: "translateY(-2px)",
          transition: "all 0.3s ease",
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <TransactionsFilters onSubmit={handleSearchClick} />
      </Card>

      <Table<ITransaction>
        rowKey={(record) => record.authorization.info.transaction.id }
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        style={{marginTop:'1em'}}
        dataSource={transactions ?? []}
        scroll={{ x: true }}
        columns={columns}
        loading={isLoading}
        pagination={{
          total: pageInfo.total,
          defaultPageSize: 10,
          defaultCurrent: 1,
          onChange: handlePaginationChange,
          pageSizeOptions: ['10', '25', '50', '100'],
          showSizeChanger: true,
        }}
      />

      <div style={{ width: 'max-content', maxWidth: '100%', margin: 'auto' }}>
        <Button onClick={goToPrevious} type="primary">
          Anterior
        </Button>
        <Button disabled={thirdStepData === null} onClick={goToNext} type="primary" style={{ left: '10px' }}>
          Siguiente
        </Button>
      </div>
    </div>
  );
}

export default ThirdStep;